import React, { useState, useMemo, useCallback } from 'react';
import Badge from '../ui/Badge';
import './ExpenseDashboard.css';

function formatCurrency(n) {
  return `₹ ${Number(n || 0).toLocaleString('en-IN')}`;
}

function renderReviewBadge(exp) {
  if (exp.approved) return <Badge variant="secondary">Approved</Badge>;
  if (exp.sendPaused) return <Badge variant="info">Paused</Badge>;
  if (exp.sentToAdmin === false) return <Badge variant="info">Pending send</Badge>;
  return <Badge variant="warning">Awaiting approval</Badge>;
}

export default function EmployeeExpenseHistory({
  expenses,
  onEdit,
  onDelete,
  searchPlaceholder = 'Search expenses…',
}) {
  const [search, setSearch] = useState('');
  const [expandedProjects, setExpandedProjects] = useState({});

  const filteredExpenses = useMemo(() => {
    if (!search.trim()) return expenses;
    const q = search.toLowerCase();
    return expenses.filter((e) =>
      ['projectId', 'projectName', 'expense'].some(
        (k) => String(e[k] ?? '').toLowerCase().includes(q),
      ),
    );
  }, [expenses, search]);

  const groupedByProject = useMemo(() => {
    const map = new Map();
    filteredExpenses.forEach((exp) => {
      const key = exp.projectDocId || 'unknown';
      if (!map.has(key)) {
        map.set(key, {
          projectDocId: key,
          projectName: exp.projectName || 'Unknown Project',
          projectId: exp.projectId || '—',
          expenses: [],
          total: 0,
        });
      }
      const group = map.get(key);
      group.expenses.push(exp);
      group.total += Number(exp.amount) || 0;
    });
    return Array.from(map.values()).sort((a, b) => a.projectName.localeCompare(b.projectName));
  }, [filteredExpenses]);

  const toggleProject = useCallback((projectDocId) => {
    setExpandedProjects((prev) => ({ ...prev, [projectDocId]: !prev[projectDocId] }));
  }, []);

  return (
    <div className="exp-dashboard-grouped emp-expense-history">
      <div className="exp-group-toolbar">
        <input
          type="search"
          className="ui-data-table-search"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="ui-data-table-count">
          {filteredExpenses.length} expenses · {groupedByProject.length} projects
        </span>
      </div>

      {groupedByProject.length === 0 ? (
        <p className="expense-loading">No expenses found</p>
      ) : (
        <div className="exp-project-groups">
          {groupedByProject.map((group) => {
            const isExpanded = expandedProjects[group.projectDocId] === true;
            return (
              <div key={group.projectDocId} className="exp-project-group">
                <button
                  type="button"
                  className="exp-project-group-header"
                  onClick={() => toggleProject(group.projectDocId)}
                  aria-expanded={isExpanded}
                >
                  <span className="exp-group-arrow">
                    <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`} />
                  </span>
                  <div className="exp-group-info">
                    <strong>{group.projectName}</strong>
                    <span className="exp-group-meta">
                      {group.projectId} · Total: {formatCurrency(group.total)} · Expenses: {group.expenses.length}
                    </span>
                  </div>
                </button>
                <div className={`exp-project-group-body ${isExpanded ? 'is-expanded' : ''}`}>
                  <div className="exp-group-table-wrap">
                    <table className="exp-group-table">
                      <thead>
                        <tr>
                          <th>Expense</th>
                          <th>Date</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Review</th>
                          <th>Service Provider</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.expenses.map((exp) => (
                          <tr key={exp.id} className={exp.approved ? 'is-approved-expense' : ''}>
                            <td>{exp.expense}</td>
                            <td>{exp.date}</td>
                            <td>{formatCurrency(exp.amount)}</td>
                            <td>
                              <Badge variant={exp.status === 'Paid' ? 'success' : 'warning'}>{exp.status}</Badge>
                            </td>
                            <td>{renderReviewBadge(exp)}</td>
                            <td>{exp.serviceProvidedPersonName || '—'}</td>
                            <td>
                              {exp.approved ? (
                                <span className="emp-locked-icon" title="Locked — approved expenses cannot be edited">
                                  <i className="fas fa-lock" aria-hidden="true" />
                                  <span className="sr-only">Locked</span>
                                </span>
                              ) : (
                                <div className="exp-actions">
                                  <button type="button" className="exp-icon-btn" onClick={() => onEdit(exp)} title="Edit">
                                    <i className="fas fa-edit" />
                                  </button>
                                  <button type="button" className="exp-icon-btn danger" onClick={() => onDelete(exp)} title="Delete">
                                    <i className="fas fa-trash" />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="exp-mobile-cards">
                    {group.expenses.map((exp) => (
                      <div key={exp.id} className={`exp-mobile-card ${exp.approved ? 'is-approved' : ''}`}>
                        <div className="exp-mobile-card-top">
                          <strong>{exp.expense}</strong>
                          {renderReviewBadge(exp)}
                        </div>
                        <div className="exp-mobile-card-meta">
                          <span>{exp.date}</span>
                          <span>{formatCurrency(exp.amount)}</span>
                          <Badge variant={exp.status === 'Paid' ? 'success' : 'warning'}>{exp.status}</Badge>
                        </div>
                        {exp.serviceProvidedPersonName && (
                          <div className="emp-expense-card-row">
                            <span>Service Provider</span>
                            <span>{exp.serviceProvidedPersonName}</span>
                          </div>
                        )}
                        <div className="exp-mobile-card-actions">
                          {exp.approved ? (
                            <span className="emp-locked-icon" title="Locked — approved expenses cannot be edited">
                              <i className="fas fa-lock" aria-hidden="true" />
                            </span>
                          ) : (
                            <>
                              <button type="button" className="exp-icon-btn" onClick={() => onEdit(exp)} title="Edit">
                                <i className="fas fa-edit" />
                              </button>
                              <button type="button" className="exp-icon-btn danger" onClick={() => onDelete(exp)} title="Delete">
                                <i className="fas fa-trash" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
